import type { ReactNode } from 'react';

interface CheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  label?: string;
  description?: string;
  children?: ReactNode;
  colorTheme?: 'blue' | 'purple' | 'green' | 'orange' | 'red' | 'cyan';
  className?: string;
  ariaLabel?: string;
}

const colorClasses: Record<string, string> = {
  blue:   'text-blue-600 focus:ring-blue-500',
  purple: 'text-purple-600 focus:ring-purple-500',
  green:  'text-green-600 focus:ring-green-500',
  orange: 'text-orange-600 focus:ring-orange-500',
  red:    'text-red-600 focus:ring-red-500',
  cyan:   'text-cyan-600 focus:ring-cyan-500',
};

export default function Checkbox({
  checked,
  onChange,
  disabled = false,
  label,
  description,
  children,
  colorTheme = 'blue',
  className,
  ariaLabel
}: CheckboxProps) {
  const hasContent = label !== undefined || children !== undefined;
  const hasDescription = description !== undefined;

  const inputClasses = [
    'h-4 w-4 border-gray-300 rounded cursor-pointer disabled:cursor-not-allowed',
    hasDescription ? 'mt-1' : '',
    colorClasses[colorTheme]
  ].filter(Boolean).join(' ');

  const input = (
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      disabled={disabled}
      aria-label={ariaLabel}
      className={inputClasses}
    />
  );

  if (!hasContent) {
    return input;
  }

  return (
    <label className={`flex ${hasDescription ? 'items-start gap-3' : 'items-center gap-2'} ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${className ?? ''}`}>
      {input}
      {children ?? (
        <div>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {label}
          </span>
          {hasDescription && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {description}
            </p>
          )}
        </div>
      )}
    </label>
  );
}
